import React, { useState, useEffect, useRef } from 'react';
import { HelpCircle, X } from 'lucide-react';
import { storiesApi } from '../../api/stories';
import './LoglineHeader.css';

/**
 * Editable logline header for Logline Workshop sessions.
 * Displays the story's current logline and allows inline editing.
 * Includes "About Loglines" help link for persistent access to educational content.
 */
function LoglineHeader({ storyId, initialLogline }) {
  const [logline, setLogline] = useState(initialLogline || '');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [savedLogline, setSavedLogline] = useState(initialLogline || '');
  const [showAbout, setShowAbout] = useState(false);
  const textareaRef = useRef(null);

  // Update local state when initialLogline changes
  useEffect(() => {
    setLogline(initialLogline || '');
    setSavedLogline(initialLogline || '');
  }, [initialLogline]);

  // Auto-resize textarea and focus when editing
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [isEditing, logline]);

  const handleSave = async () => {
    if (!storyId || logline === savedLogline) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await storiesApi.update(storyId, { log_line: logline });
      setSavedLogline(logline);
      setIsEditing(false);
    } catch (err) {
      console.error('Failed to save logline:', err);
      // Keep editing mode open on error
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setLogline(savedLogline);
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const hasChanges = logline !== savedLogline;

  return (
    <div className="logline-header">
      <div className="logline-header__label">
        <span className="logline-header__icon">🎯</span>
        <span className="logline-header__title">Story Logline</span>
        <button
          className="logline-header__help-btn"
          onClick={() => setShowAbout(!showAbout)}
          aria-label="About loglines"
          title="What's a logline?"
        >
          <HelpCircle size={16} />
        </button>
      </div>

      {/* About Loglines expandable section */}
      {showAbout && (
        <div className="logline-header__about">
          <div className="logline-header__about-content">
            <p>
              <strong>A logline</strong> is a 2-3 sentence hook for your story—the pitch that makes
              someone want to read more. It captures who your protagonist is, what situation they
              face, and what's at stake.
            </p>
            <p>
              Think of it as the question your story poses, not the answer. In Brainstormy, your
              logline becomes a touchstone for all your creative work.
            </p>
          </div>
          <button
            className="logline-header__about-close"
            onClick={() => setShowAbout(false)}
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>
      )}

      <div className="logline-header__content">
        {isEditing ? (
          <textarea
            ref={textareaRef}
            className="logline-header__textarea"
            value={logline}
            onChange={(e) => setLogline(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Your story's premise will appear here as you develop it..."
            disabled={isSaving}
          />
        ) : (
          <div
            className="logline-header__display"
            onClick={() => setIsEditing(true)}
          >
            {savedLogline || (
              <span className="logline-header__placeholder">
                Click to add your story's logline...
              </span>
            )}
          </div>
        )}
      </div>

      <div className="logline-header__actions">
        {isEditing ? (
          <>
            <button
              className="logline-header__btn logline-header__btn--cancel"
              onClick={handleCancel}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              className="logline-header__btn logline-header__btn--save"
              onClick={handleSave}
              disabled={isSaving || !hasChanges}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </>
        ) : (
          <button
            className="logline-header__btn logline-header__btn--edit"
            onClick={() => setIsEditing(true)}
          >
            Edit
          </button>
        )}
      </div>
    </div>
  );
}

export default LoglineHeader;
